import type { SvgIconComponent } from '@mui/icons-material';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import SmartphoneOutlined from '@mui/icons-material/SmartphoneOutlined';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import BarChartOutlined from '@mui/icons-material/BarChartOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import type { Whoami } from '../api/whoami';

/** Which module (if any) gates a nav item's visibility. */
export type NavModule = 'insights' | 'requests';

/** A Settings sub-nav entry (SectionNav). */
export interface NavChild {
  key: string;
  labelKey: string;
  to: string;
}

/** A top-level sidebar nav item. */
export interface NavItem {
  key: string;
  labelKey: string;
  to: string;
  icon: SvgIconComponent;
  /** When set, the item is hidden unless the module is enabled. */
  module?: NavModule;
  /** When present, the item expands to a Settings-style sub-nav. */
  children?: NavChild[];
  /** Optional count badge (e.g. pending approvals) rendered on the row. */
  badge?: number;
}

/**
 * Which workspace a **dual-role** internal-staff user (Administrator *and*
 * Manager) is viewing. The sidebar role switcher toggles this; single-role
 * users ignore it. Not persisted — resets to the default each session.
 */
export type WorkspaceView = 'admin' | 'manager';

/**
 * Context the {@link AppShell} publishes through its `<Outlet/>` so routed pages
 * can render workspace-aware content (e.g. the shared `/requests` route renders
 * the Administrator list vs. the Manager approval queue). `workspace` is
 * `undefined` for single-role users (the page derives the view from `whoami`).
 */
export interface ShellOutletContext {
  workspace?: WorkspaceView;
}

const SETTINGS_CHILDREN: NavChild[] = [
  { key: 'catalog', labelKey: 'settings.catalog', to: '/settings/catalog' },
  { key: 'budgets', labelKey: 'settings.budgets', to: '/settings/budgets' },
  { key: 'notifications', labelKey: 'settings.notifications', to: '/settings/notifications' },
  { key: 'data', labelKey: 'settings.data', to: '/settings/data' },
];

/**
 * "My lines" — the signed-in person's own lines, in every workspace.
 *
 * Shared by all three navs because it describes the person rather than the role: an
 * Administrator or a Manager carries a phone like anyone else, and until now only the User
 * workspace offered a way to see it. The route was already reachable and already
 * role-agnostic; only the nav entry was missing.
 *
 * Not gated on actually having lines. The page answers that itself with an empty state,
 * which is the same shape `/team` uses for a non-manager — a scoped read returns 200 with
 * nothing in it rather than 403, and the screen says so.
 */
const MY_LINES_ITEM: NavItem = {
  key: 'myLines',
  labelKey: 'nav.myLines',
  to: '/my-lines',
  icon: SmartphoneOutlined,
  module: 'insights',
};

/**
 * Administrator nav (F2): Dashboard, Lines, Users, Requests, Reports, Invoices,
 * Settings. Settings expands to the Catalog/Budgets/Notifications/Data sub-nav.
 */
const ADMIN_NAV: NavItem[] = [
  {
    key: 'dashboard',
    labelKey: 'nav.dashboard',
    to: '/dashboard',
    icon: DashboardOutlined,
    module: 'insights',
  },
  {
    key: 'lines',
    labelKey: 'nav.lines',
    to: '/lines',
    icon: SmartphoneOutlined,
    module: 'insights',
  },
  // An Administrator is also a person who may carry a line. `lines` is every line the
  // customer holds; this is the handful assigned to them, with their own budget standing.
  // Sits next to `lines` because the two answer neighbouring questions.
  MY_LINES_ITEM,
  { key: 'users', labelKey: 'nav.users', to: '/users', icon: PeopleOutlined },
  {
    key: 'requests',
    labelKey: 'nav.requests',
    to: '/requests',
    icon: DescriptionOutlined,
    module: 'requests',
  },
  {
    key: 'reports',
    labelKey: 'nav.reports',
    to: '/reports',
    icon: BarChartOutlined,
    module: 'insights',
  },
  {
    key: 'invoices',
    labelKey: 'nav.invoices',
    to: '/invoices',
    icon: ReceiptLongOutlined,
    module: 'insights',
  },
  {
    key: 'settings',
    labelKey: 'nav.settings',
    to: '/settings',
    icon: SettingsOutlined,
    children: SETTINGS_CHILDREN,
  },
];

/** Manager nav (F3): Dashboard, My team, Team lines, My lines, Requests, Reports. */
const MANAGER_NAV: NavItem[] = [
  {
    key: 'dashboard',
    labelKey: 'nav.dashboard',
    to: '/dashboard',
    icon: DashboardOutlined,
    module: 'insights',
  },
  { key: 'team', labelKey: 'nav.myTeam', to: '/team', icon: GroupsOutlined },
  // The Administrator's `lines` inventory, bounded to the caller's reporting
  // sub-tree. Insights-gated exactly as the Administrator entry is, so the two
  // appear and disappear together for a customer that has Insights off — the INV
  // asset read behind both is not itself module-gated, and the page degrades to
  // asset-only columns if it is ever reached with Insights disabled.
  {
    key: 'teamLines',
    labelKey: 'nav.teamLines',
    to: '/team/lines',
    icon: SmartphoneOutlined,
    module: 'insights',
  },
  // A Manager's own lines, distinct from the team roster above it.
  MY_LINES_ITEM,
  {
    key: 'requests',
    labelKey: 'nav.requests',
    to: '/requests',
    icon: DescriptionOutlined,
    module: 'requests',
  },
  {
    key: 'reports',
    labelKey: 'nav.reports',
    to: '/reports',
    icon: BarChartOutlined,
    module: 'insights',
  },
];

/** User nav (F3): Dashboard ("My usage"), My lines, Requests. */
const USER_NAV: NavItem[] = [
  {
    key: 'dashboard',
    labelKey: 'nav.myUsage',
    to: '/dashboard',
    icon: DashboardOutlined,
    module: 'insights',
  },
  MY_LINES_ITEM,
  {
    key: 'requests',
    labelKey: 'nav.requests',
    to: '/requests',
    icon: DescriptionOutlined,
    module: 'requests',
  },
];

/**
 * True for a customer user who genuinely holds BOTH workspaces: the Administrator
 * role AND the computed Manager role (i.e. they have direct reports).
 *
 * Keys off the effective **role set**, not the `isAdministrator`/`isManager`
 * permission flags. Those flags carry the role hierarchy — Administrator
 * *implies* Manager — so `effective.isManager` is `true` for every Administrator
 * and would light the workspace switcher for admins who manage no one. The
 * backend adds `Manager` to `roles` only when it computed real direct reports
 * (IDN `AssembleRoles` / `ManagerRoleComputer`), so the role set is the faithful
 * signal for "has a Manager workspace".
 */
export function isDualRole(whoami: Whoami | null | undefined): boolean {
  if (!whoami) return false;
  const { roles } = whoami.effective;
  return roles.includes('Administrator') && roles.includes('Manager');
}

/**
 * Picks the role-appropriate nav set from the effective identity. For dual-role
 * users the `view` (from the sidebar role switcher) selects Administrator vs
 * Manager; otherwise the effective role decides (Administrator wins).
 */
function roleNav(whoami: Whoami, view?: WorkspaceView): NavItem[] {
  const { effective } = whoami;
  // The Manager *workspace* is gated on genuinely managing people (the computed
  // Manager role in the set), not the `isManager` permission flag that every
  // Administrator carries by implication — see isDualRole.
  const managesTeam = effective.roles.includes('Manager');
  if (view === 'manager' && managesTeam) {
    return MANAGER_NAV;
  }
  if (view === 'admin' && effective.isAdministrator) {
    return ADMIN_NAV;
  }
  if (effective.isAdministrator) {
    return ADMIN_NAV;
  }
  if (managesTeam) {
    return MANAGER_NAV;
  }
  return USER_NAV;
}

function moduleEnabled(item: NavItem, whoami: Whoami): boolean {
  if (!item.module) {
    return true;
  }
  const settings = whoami.moduleSettings;
  // No settings ⇒ no customer scope (bare SiteAdmin) ⇒ don't hide.
  if (!settings) {
    return true;
  }
  return item.module === 'insights' ? settings.isInsightsEnabled : settings.isRequestsEnabled;
}

/**
 * Builds the ordered sidebar nav for the given identity: the role set (from the
 * effective role flags) filtered by module enablement. Disabled-module items
 * are removed entirely — never greyed out (WEB PRD §2.2, area doc 00 §Module
 * visibility).
 */
export function buildNav(whoami: Whoami, view?: WorkspaceView): NavItem[] {
  return roleNav(whoami, view).filter((item) => moduleEnabled(item, whoami));
}

/** The i18n key for the sidebar workspace label (account menu + row footer). */
export function workspaceLabelKey(whoami: Whoami | null | undefined, view?: WorkspaceView): string {
  if (!whoami) {
    return 'shell.workspace.user';
  }
  const { effective } = whoami;
  const managesTeam = effective.roles.includes('Manager');
  if (view === 'manager' && managesTeam) {
    return 'shell.workspace.manager';
  }
  if (view === 'admin' && effective.isAdministrator) {
    return 'shell.workspace.administrator';
  }
  if (effective.isAdministrator) {
    return 'shell.workspace.administrator';
  }
  if (managesTeam) {
    return 'shell.workspace.manager';
  }
  return 'shell.workspace.user';
}
